import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Radio,
  Text,
  Tooltip,
  useTheme,
} from '@chakra-ui/react';
import { RadioGroupInput } from '../../../formComponents/radioInput';
import {
  ContributorTableFiltersProps,
  MetricFilterState,
} from '../types/contributorTableTypes';
import MetricAdvancedFilter from '../../../FormikComponents/metricAdvancedFilter';
import CustomSelect, { ISelect } from '../../../formComponents/customSelect';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useContributorTableStore } from '../../../hooks/useContributorTableStore';
import { EraserIcon } from '../../../icons';
import { useToastHook } from '../../../appToast';
import { useGlobalState } from '../../../hooks/useGlobalState';
import { getUser } from '../../common/api/query';
import Loader from '../../../appLoader';
import { MenuListProps, OptionProps } from 'chakra-react-select';
import { useTeamHierarchy } from '../../../hooks/useTeamHierarchy';
import { TeamHierarchySelectOption } from '../../../TeamHierarchySelectOption';

interface TeamOptionWithMetadata {
  value: string;
  label: string;
  depth?: number;
  teamPath?: string;
  hasNestedSubTeams?: boolean;
}
export function ContributorTableFilters({
  teamState,
  setTeamState,
  teamOptions,
  selectedMetrics,
  metricsList,
  compareTo,
  setCompareTo,
  repoOptions,
  languageOptions,
  jobRoleOptions,
  jobRoleState,
  setJobRoleState,
  repoState,
  metricFilterState,
  setMetricFilterState,
  setRepoState,
  languageState,
  setLanguageState,
  dynamicFilterOptions,
  activityType,
  handleActivityFilterChange,
  configureMetricsModalOpen,
  slideBarOnClose,
  showContributorPercentiles,
  setShowContributorPercentiles,
  saveFilters,
  saveFiltersIsLoading,
}: ContributorTableFiltersProps) {
  const { colors } = useTheme();
  const [setToast] = useToastHook();
  const { pathname } = useLocation();
  const [getUserLoading, setGetUserLoading] = useState(false);
  const { globalState, setGlobalState } = useGlobalState();
  const [metricFilterStateLocal, setMetricFilterStateLocal] =
    useState<MetricFilterState[]>(metricFilterState);
  const [errorMetricFields, setErrorMetricFields] = useState<
    MetricFilterState[]
  >([]);

  const { setIsContributorsEmpty } = useContributorTableStore();
  const [activityTypeLocal, setActivityTypeLocal] = useState(
    activityType.value
  );
  const [compareToLocal, setCompareToLocal] = useState(compareTo);
  const [teamStateLocal, setTeamStateLocal] = useState(teamState || []);
  const [repoStateLocal, setRepoStateLocal] = useState(repoState);
  const [languageStateLocal, setLanguageStateLocal] = useState(languageState);
  const [jobRoleStateLocal, setJobRoleStateLocal] = useState(jobRoleState);
  const [showContributorPercentilesLocal, setShowContributorPercentilesLocal] =
    useState(showContributorPercentiles);

  const allTeamOptions: TeamOptionWithMetadata[] = useMemo(
    () => (teamOptions || []) as TeamOptionWithMetadata[],
    [teamOptions]
  );

  const {
    expandedTeamPaths,
    toggleTeamExpand,
    visibleTeams: visibleTeamOptions,
  } = useTeamHierarchy(allTeamOptions);

  const validateFilterData = (data: any) => {
    if (data.teams && !Array.isArray(data.teams)) {
      return false;
    }
    if (data.compareTo && typeof data.compareTo !== 'string') {
      return false;
    }
    if (
      data.contributorPercentiles &&
      typeof data.contributorPercentiles !== 'boolean'
    ) {
      return false;
    }
    if (data.gitActivity && typeof data.gitActivity !== 'string') {
      return false;
    }
    if (data.repos && !Array.isArray(data.repos)) {
      return false;
    }
    if (data.languages && !Array.isArray(data.languages)) {
      return false;
    }
    if (data.jobRoles && !Array.isArray(data.jobRoles)) {
      return false;
    }
    if (data.metricFilterState && !Array.isArray(data.metricFilterState)) {
      return false;
    }
    return true;
  };

  const handleSaveFilters = (filteredMetrics: MetricFilterState[]) => {
    if (!Array.isArray(filteredMetrics) || !saveFilters) {
      return;
    }
    const filterData = {
      teams: teamStateLocal || [],
      gitActivity: activityTypeLocal,
      compareTo:
        !showContributorPercentilesLocal && compareToLocal === 'pastPeriod'
          ? 'pastPeriod'
          : '',
      contributorPercentiles: showContributorPercentilesLocal,
      repos: repoStateLocal,
      languages: languageStateLocal,
      jobRoles: jobRoleStateLocal,
      metricFilterState: filteredMetrics,
    };

    if (!validateFilterData(filterData)) {
      setToast({
        message: 'Invalid filter data',
        status: 'error',
      });
      return;
    }
    saveFilters(
      {
        filter: filterData,
      },
      {
        onSuccess: () => {
          setGetUserLoading(true);
          getUser()
            .then((res) => {
              setGetUserLoading(false);
              setGlobalState({ ...globalState, dto: res.dto });
              setMetricFilterState(filteredMetrics);
              handleActivityFilterChange(activityTypeLocal);
              setCompareTo(compareToLocal as 'pastPeriod' | '');
              setRepoState(repoStateLocal);
              setLanguageState(languageStateLocal);
              setTeamState?.(teamStateLocal);
              setShowContributorPercentiles(showContributorPercentilesLocal);
              setJobRoleState(jobRoleStateLocal);
              setIsContributorsEmpty(false);
              setToast({
                message: 'Filters saved successfully',
                status: 'success',
              });
              slideBarOnClose();
            })
            .catch(() => {
              setGetUserLoading(false);
              setToast({
                message: 'Failed to save filters',
                status: 'error',
              });
            });
        },
        onError: () => {
          setGetUserLoading(false);
          setToast({
            message: 'Failed to save filters',
            status: 'error',
          });
        },
      }
    );
  };
  return (
    <Flex direction={'column'} gap={5} position={'relative'}>
      {(getUserLoading || saveFiltersIsLoading) && <Loader />}
      <Box>
        <Text
          fontSize="sm"
          fontFamily="heading"
          fontWeight="medium"
          color="text.secondary"
          mb={1}
        >
          Git Activity
        </Text>

        <RadioGroupInput
          options={dynamicFilterOptions}
          value={activityTypeLocal}
          onChange={(e: string) => {
            setActivityTypeLocal(e);
          }}
        />
      </Box>{' '}
      <Flex
        borderTop={'1px solid #E0E0E0'}
        pt={5}
        flexDirection={'column'}
        gap={2}
      >
        <Flex align={'center'} gap={1}>
          <Text
            fontSize="sm"
            fontFamily="heading"
            fontWeight="medium"
            color="text.secondary"
            lineHeight="17px"
            mb={1}
          >
            Compare Options
          </Text>
          <Tooltip label="Clear" hasArrow>
            <IconButton
              variant={'ghost'}
              size={'xs'}
              aria-label="Clear"
              icon={<EraserIcon />}
              onClick={() => {
                setCompareToLocal('');
                setShowContributorPercentilesLocal(false);
              }}
            />
          </Tooltip>
        </Flex>
        <Box>
          <Radio
            defaultChecked={compareToLocal === 'pastPeriod'}
            isChecked={compareToLocal === 'pastPeriod'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setCompareToLocal(e.target.checked ? 'pastPeriod' : '');
              if (e.target.checked) {
                setShowContributorPercentilesLocal(false);
              }
            }}
          >
            <Text
              fontSize={'sm'}
              color={colors.text.secondary}
              fontWeight={'normal'}
            >
              Compare to Past Period
            </Text>
          </Radio>
        </Box>
        <Box>
          <Radio
            defaultChecked={showContributorPercentilesLocal}
            isChecked={showContributorPercentilesLocal}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setShowContributorPercentilesLocal(e.target.checked);
              if (e.target.checked) {
                setCompareToLocal('');
              }
            }}
          >
            <Text
              fontSize={'sm'}
              color={colors.text.secondary}
              fontWeight={'normal'}
            >
              Compare Contributors
            </Text>
          </Radio>
        </Box>
      </Flex>
      <Flex
        borderTop={'1px solid #E0E0E0'}
        pt={5}
        gap={4}
        flexDirection={'column'}
      >
        <Text
          fontSize="sm"
          fontFamily="heading"
          fontWeight="medium"
          color="text.secondary"
          lineHeight="17px"
        >
          Codebase
        </Text>
        <Box>
          <Text
            fontSize="xs"
            fontFamily="heading"
            fontWeight="medium"
            color="text.secondary"
            lineHeight="17px"
            mb={2}
          >
            Repositories
          </Text>

          <CustomSelect
            maxWidth={'350px'}
            placeholder="Select Repositories"
            isMulti={true}
            useBasicStyles
            onChange={(e: ISelect[]) => {
              setRepoStateLocal(e);
            }}
            size="sm"
            value={repoStateLocal}
            options={repoOptions}
          />
        </Box>
        <Box>
          <Text
            fontSize="xs"
            fontFamily="heading"
            fontWeight="medium"
            color="text.secondary"
            lineHeight="17px"
            mb={2}
          >
            Languages
          </Text>

          <CustomSelect
            maxWidth={'350px'}
            placeholder="Select Languages"
            isMulti={true}
            useBasicStyles
            onChange={(e: ISelect[]) => {
              setLanguageStateLocal(e);
            }}
            size="sm"
            value={languageStateLocal}
            options={languageOptions}
          />
        </Box>
      </Flex>
      <Flex
        borderTop={'1px solid #E0E0E0'}
        pt={5}
        gap={4}
        flexDirection={'column'}
      >
        <Text
          fontSize="sm"
          fontFamily="heading"
          fontWeight="medium"
          color="text.secondary"
          lineHeight="17px"
        >
          Organization
        </Text>

        <Box>
          <Text
            fontSize="xs"
            fontFamily="heading"
            fontWeight="medium"
            color="text.secondary"
            lineHeight="17px"
            mb={2}
          >
            Job Roles
          </Text>

          <CustomSelect
            maxWidth={'350px'}
            placeholder="Select Job Roles"
            isMulti={true}
            useBasicStyles
            onChange={(e: ISelect[]) => {
              setJobRoleStateLocal(e);
            }}
            size="sm"
            value={jobRoleStateLocal}
            options={jobRoleOptions}
          />
        </Box>
        {teamState && teamOptions && (
          <Box>
            <Text
              fontSize="xs"
              fontFamily="heading"
              fontWeight="medium"
              color="text.secondary"
              lineHeight="17px"
              mb={2}
            >
              Teams
            </Text>

            <CustomSelect
              chakraStyles={{
                menu: (provided: MenuListProps) => ({
                  ...provided,
                  zIndex: 9999,
                }),
                option: (provided: OptionProps, state: any) => {
                  const depth: number =
                    (state.data as TeamOptionWithMetadata)?.depth ?? 0;
                  return {
                    ...provided,
                    paddingLeft: `${12 + depth * 20}px`,
                  };
                },
              }}
              maxWidth={'350px'}
              placeholder="Select Teams"
              isMulti={true}
              useBasicStyles
              onChange={(e: ISelect[]) => {
                setTeamStateLocal(e);
              }}
              size="sm"
              value={teamStateLocal}
              options={visibleTeamOptions}
              expandedTeamPaths={expandedTeamPaths}
              toggleTeamExpand={toggleTeamExpand}
              components={{ Option: TeamHierarchySelectOption }}
            />
          </Box>
        )}
      </Flex>
      <Flex
        borderTop={'1px solid #E0E0E0'}
        pt={5}
        flexDirection={'column'}
        gap={1}
      >
        <Flex align={'center'} justify={'space-between'} mb={2}>
          <Text
            fontSize="sm"
            fontFamily="heading"
            fontWeight="medium"
            color="text.secondary"
            lineHeight="17px"
            mb={2}
          >
            Metric Filters
          </Text>
          <Button
            variant={'primary'}
            size={'sm'}
            onClick={() => {
              configureMetricsModalOpen?.();
            }}
          >
            Add Metrics
          </Button>
        </Flex>
        <Box>
          <MetricAdvancedFilter
            metricsList={metricsList}
            selectedMetrics={selectedMetrics}
            metricFilterState={metricFilterStateLocal}
            errorMetricFields={errorMetricFields}
            setErrorMetricFields={setErrorMetricFields}
            setMetricFilterState={setMetricFilterStateLocal}
          />
        </Box>
      </Flex>
      <Button
        variant={'primary'}
        position={'sticky'}
        bottom={0}
        zIndex={10}
        w={'full'}
        _disabled={{
          color: 'text.secondary',
          borderColor: 'text.secondary',
          _hover: {
            bg: 'white',
            cursor: 'not-allowed',
          },
        }}
        isDisabled={saveFiltersIsLoading || getUserLoading}
        onClick={() => {
          const errorMetricFields = metricFilterStateLocal.filter(
            (metric: any) => {
              if (metric.metricOperator === 'BETWEEN') {
                const min = metric.targetValue?.toString().split('-')[0];
                const max = metric.targetValue?.toString().split('-')[1];
                if (!min || !max) {
                  return true;
                }
                if (min && max && parseInt(min) > parseInt(max)) {
                  return true;
                }
                if (min && max && parseInt(min) === parseInt(max)) {
                  return true;
                }
              }
              const isMissing =
                metric.targetValue === null ||
                metric.targetValue === undefined ||
                metric.targetValue === '' ||
                Number.isNaN(metric.targetValue);

              const hasError =
                (isMissing && metric.metricOperator) ||
                (!isMissing && !metric.metricOperator);

              return hasError;
            }
          );

          if (errorMetricFields.length > 0) {
            setErrorMetricFields(errorMetricFields);
            return;
          }

          const filteredMetrics = metricFilterStateLocal.filter(
            (metric: any) =>
              metric.targetValue !== '' && metric.metricOperator !== ''
          );
          setErrorMetricFields([]);

          if (pathname.split('/')[1] !== 'teams') {
            handleSaveFilters(filteredMetrics);
            return;
          }

          setMetricFilterState(filteredMetrics);
          handleActivityFilterChange(activityTypeLocal);
          setCompareTo(compareToLocal as 'pastPeriod' | '');
          setRepoState(repoStateLocal);
          setLanguageState(languageStateLocal);
          setShowContributorPercentiles(showContributorPercentilesLocal);
          setJobRoleState(jobRoleStateLocal);
          setTeamState?.(teamStateLocal);
          setIsContributorsEmpty(false);
          slideBarOnClose();
        }}
      >
        {pathname.split('/')[1] !== 'teams' ? 'Save Filters' : 'Apply Filters'}
      </Button>
    </Flex>
  );
}

export default ContributorTableFilters;
